import React, {useCallback, useEffect, useState} from "react";
import {connect} from "react-redux";
import {
    Checkbox,
    DialogActions,
    DialogContent,
    DialogTitle, Divider, List, ListItemButton, ListItemIcon, ListItemText, Alert, Typography,
    TextField,
    useMediaQuery,
    useTheme,
    Box,
    Button
} from "@mui/material";
import {useForm} from "react-hook-form";
import {userActions} from "../../store/actions/userAction";
import RoleSelect from "./RoleSelect";
import {workspaceActions} from "../../store/actions/workspaceAction";
import Spinner from "../spinner/Spinner";
import {SettingsDialog as Dialog, SettingsDialogDescription, SettingsFormSection, settingsSelectionListStyles} from "../settings/SettingsDialog";

export function AddMember(props) {
    const theme = useTheme();

    const {invite, readWorkspaces} = props;
    const {workspace, alert} = props.state;

    const [checked, setChecked] = useState([]);

    const {register, handleSubmit, control, formState: {errors}} = useForm();

    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

    const handleOnSubmit = (data) => {
        let workspaceIds = "";
        checked?.forEach((ws) => {
            if (workspaceIds !== "") {
                workspaceIds += "," + ws
            } else {
                workspaceIds += ws
            }
        });

        let result = {
            email: data?.email,
            roleId: data?.role?.id,
            workspaceIds: workspaceIds
        };
        invite(result);
    };

    const handleClose = useCallback((event, reason) => {
        if (reason === 'backdropClick') {
            return false;
        }
        props.history.push({pathname: "/settings/team/members", state: {from: props.location}});
    }, [props]);

    const handleToggle = (value) => () => {
        const currentIndex = checked.indexOf(value);
        const newChecked = [...checked];

        if (currentIndex === -1) {
            newChecked.push(value);
        } else {
            newChecked.splice(currentIndex, 1);
        }

        setChecked(newChecked);
    };

    const handleToggleAll = () => {
        if (checked?.length !== 0 && checked?.length === workspace?.workspaces?.length) {
            setChecked([]);
        } else {
            let newOptions = [];
            workspace?.workspaces?.forEach((item) => {
                newOptions.push(item?.id);
            });
            setChecked(newOptions);
        }
    };

    useEffect(() => {
        readWorkspaces({workspaceTypes: "master,private"});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (alert?.successMsg !== "") {
            props.history.push({pathname: "/settings/team/members", state: {from: props.location}});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [alert?.successMsg])

    const workspaces = workspace?.workspaces || [];
    const allSelected = workspaces.length > 0 && checked.length === workspaces.length;
    return <Dialog open onClose={handleClose} fullWidth fullScreen={fullScreen} maxWidth="sm" aria-labelledby="invite-member-form">
        <DialogTitle id="invite-member-form">Invite a new member
            <SettingsDialogDescription>Invite someone to your team and choose their role and workspace access.</SettingsDialogDescription>
        </DialogTitle>
        <DialogContent>
            {alert.errorMsg && <Alert severity="error" variant="filled" sx={{mb: 2}}>{alert.errorMsg}</Alert>}
            <Box component="form" onSubmit={handleSubmit(handleOnSubmit)} autoComplete="off" sx={{display: 'grid', gap: 1.5}}>
                <TextField error={!!errors.email} helperText={errors.email ? 'Enter a valid email address.' : undefined}
                    variant="outlined" margin="dense" id="email" name="email" size="small" type="email"
                    placeholder="Enter an email address" fullWidth label="Email address"
                    slotProps={{inputLabel: {shrink: true}}}
                    {...register("email", {required: true,
                        pattern: /^[a-zA-Z\d][a-zA-Z\d-_.]+@([a-zA-Z]|[a-zA-Z\d]?[a-zA-Z\d-]+[a-zA-Z\d])\.[a-zA-Z\d]{2,10}(?:\.[a-zA-Z]{2,10})?$/,
                        maxLength: 255})}/>
                <RoleSelect errors={errors} control={control}/>
            </Box>
            <SettingsFormSection title="Workspace access" description="All members get access to the Public workspace by default.">
                <List sx={settingsSelectionListStyles} aria-label="Workspace access">
                    {workspace?.loading?.read && <Spinner overComponentBox noBackShadow/>}
                    {workspaces.length > 0 && <>
                        <ListItemButton onClick={handleToggleAll}>
                            <ListItemIcon><Checkbox size="small" checked={allSelected} indeterminate={checked.length > 0 && !allSelected}
                                tabIndex={-1} disableRipple slotProps={{input: {'aria-labelledby': 'all-workspaces'}}}/></ListItemIcon>
                            <ListItemText id="all-workspaces" primary="Select all workspaces" secondary={`${checked.length} of ${workspaces.length} selected`}/>
                        </ListItemButton>
                        <Divider/>
                    </>}
                    {workspaces.map(item => <ListItemButton key={item.id} onClick={handleToggle(item.id)}>
                        <ListItemIcon><Checkbox size="small" checked={checked.includes(item.id)} tabIndex={-1} disableRipple
                            slotProps={{input: {'aria-labelledby': `workspace-${item.id}`}}}/></ListItemIcon>
                        <ListItemText id={`workspace-${item.id}`} primary={item.name}/>
                    </ListItemButton>)}
                    {!workspace?.loading?.read && !workspaces.length && <Typography sx={{p: 2, fontSize: 13, color: 'text.secondary'}}>No additional workspaces available.</Typography>}
                </List>
            </SettingsFormSection>
        </DialogContent>
        <DialogActions>
            <Button onClick={handleClose} color="inherit">Cancel</Button>
            <Button onClick={handleSubmit(handleOnSubmit)} variant="contained">Invite member</Button>
        </DialogActions>
    </Dialog>;
}

const mapStateToProps = (state) => {
    return {
        state: {
            workspace: state.workspace,
            alert: state.alert
        }
    }
};

const mapDispatchToProps = {
    invite: userActions.invite,
    readWorkspaces: workspaceActions.read,
};

export default connect(mapStateToProps, mapDispatchToProps)(AddMember);